import React from "react";
import {
  Chip,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Typography,
} from "@material-ui/core";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { setActiveStep } from "../../context/bulkUploadActions";
import { useBulkUpload } from "../../hooks/useBulkUpload";
import SummaryCard from "../shared/SummaryCard";
import { getProgramCourseFee } from "../../utils/programFee";

const useStyles = makeStyles((theme) => ({
  expansionPanel: {
    marginBottom: theme.spacing(2),
    "&:before": {
      display: "none",
    },
  },
  expansionPanelSummary: {
    backgroundColor: theme.palette.grey[50],
    "&.Mui-expanded": {
      minHeight: 48,
    },
  },
  chip: {
    marginLeft: theme.spacing(1),
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  label: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  value: {
    color: theme.palette.text.primary,
  },
}));

export const ReviewStep = ({ programDetails }) => {
  const classes = useStyles();
  const { state, dispatch } = useBulkUpload();
  const { validation } = state;
  const { summary, results } = validation;

  // Safely access warnings with fallback
  const warnings = validation.warnings || [];
  const validRecords = results?.valid || [];

  const handleBack = () => {
    dispatch(setActiveStep(1));
  };

  const handleConfirm = () => {
    dispatch(setActiveStep(3));
  };

  // Group students by status
  const newStudents = validRecords.filter((v) => v.student?.isNew);
  const existingStudents = validRecords.filter((v) => !v.student?.isNew);
  const studentsWithWarnings = validRecords.filter((v) =>
    warnings.some((w) => w.row === v.row)
  );

  const { program, courses } = programDetails;
  const course = courses?.[0];
  const { problem: feeProblem } = getProgramCourseFee(programDetails);

  return (
    <div className="review-step">
      {/* Page Header */}
      <div className="mb-6 text-center">
        <h4>Review Upload Details</h4>
        <p className="text-muted">
          Please review the summary below and confirm to proceed with enrollment
        </p>
      </div>

      {/* Summary Statistics */}
      <div className="row mb-6">
        <div className="col-md-3">
          <SummaryCard
            title="Total Students"
            value={summary?.totalStudents || 0}
            color="primary"
            icon="fa-users"
          />
        </div>
        <div className="col-md-3">
          <SummaryCard
            title="New Students"
            value={summary?.newStudents || 0}
            color="success"
            icon="fa-user-plus"
          />
        </div>
        <div className="col-md-3">
          <SummaryCard
            title="Existing Students"
            value={summary?.existingStudents || 0}
            color="info"
            icon="fa-user-check"
          />
        </div>
        <div className="col-md-3">
          <SummaryCard
            title="Warnings"
            value={summary?.warningRows || 0}
            color="warning"
            icon="fa-exclamation-triangle"
          />
        </div>
      </div>

      <div className="row mb-6">
        <div className="col-md-6">
          <SummaryCard
            title="New Parent Accounts"
            value={summary?.newParents || 0}
            color="success"
            icon="fa-user-plus"
          />
        </div>
        <div className="col-md-6">
          <SummaryCard
            title="Existing Parents"
            value={summary?.existingParents || 0}
            color="info"
            icon="fa-user-check"
          />
        </div>
      </div>

      {/* Program Information */}
      <div className="card card-custom mb-6">
        <div className="card-header">
          <div className="card-title">
            <h3 className="card-label">Enrollment Program</h3>
          </div>
        </div>
        <div className="card-body">
          <div className={classes.detailRow}>
            <span className={classes.label}>Program:</span>
            <span className={classes.value}>{course?.course_name}</span>
          </div>
          <div className={classes.detailRow}>
            <span className={classes.label}>Course Code:</span>
            <span className={classes.value}>{course?.course_code}</span>
          </div>
          <div className={classes.detailRow}>
            <span className={classes.label}>School:</span>
            <span className={classes.value}>{program?.school_name}</span>
          </div>
          <div className={classes.detailRow}>
            <span className={classes.label}>Day:</span>
            <span className={classes.value}>{program?.day}</span>
          </div>
          <div className={classes.detailRow}>
            <span className={classes.label}>Program Dates:</span>
            <span className={classes.value}>
              {program?.startdate &&
                new Date(program.startdate).toLocaleDateString()}{" "}
              to{" "}
              {program?.enddate &&
                new Date(program.enddate).toLocaleDateString()}
            </span>
          </div>
          <div className={classes.detailRow}>
            <span className={classes.label}>Sessions:</span>
            <span className={classes.value}>{program?.sessions}</span>
          </div>
        </div>
      </div>

      {/* Warnings Section */}
      {warnings.length > 0 && (
        <div className="alert alert-warning mb-6">
          <div className="alert-icon">
            <i className="flaticon-warning"></i>
          </div>
          <div className="alert-text">
            <strong>Please Review Warnings</strong>
            <p className="mb-2 mt-2">
              There are {warnings.length} warning(s) that require your
              attention:
            </p>
            <ul className="mb-0">
              {warnings.map((warning, idx) => (
                <li key={idx}>
                  <strong>Row {warning.row}:</strong> {warning.message}
                  <br />
                  <small className="text-muted">{warning.details}</small>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* What Will Happen Section */}
      <div className="card card-custom mb-6">
        <div className="card-header">
          <div className="card-title">
            <h3 className="card-label">Actions to be Performed</h3>
          </div>
        </div>
        <div className="card-body">
          <div className="timeline timeline-3">
            <div className="timeline-items">
              {/* Create New Parents */}
              {summary?.newParents > 0 && (
                <div className="timeline-item">
                  <div className="timeline-media bg-success">
                    <i className="fa fa-user-plus text-white"></i>
                  </div>
                  <div className="timeline-content">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="mr-2">
                        <span className="text-dark-75 text-hover-primary font-weight-bold">
                          Create {summary.newParents} New Parent Account(s)
                        </span>
                      </div>
                    </div>
                    <p className="text-muted mb-0">
                      New user accounts will be created with parent role
                    </p>
                  </div>
                </div>
              )}

              {/* Link Existing Parents */}
              {summary?.existingParents > 0 && (
                <div className="timeline-item">
                  <div className="timeline-media bg-info">
                    <i className="fa fa-link text-white"></i>
                  </div>
                  <div className="timeline-content">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="mr-2">
                        <span className="text-dark-75 text-hover-primary font-weight-bold">
                          Link to {summary.existingParents} Existing Parent(s)
                        </span>
                      </div>
                    </div>
                    <p className="text-muted mb-0">
                      Students will be linked to existing parent accounts
                    </p>
                  </div>
                </div>
              )}

              {/* Create New Students */}
              {summary?.newStudents > 0 && (
                <div className="timeline-item">
                  <div className="timeline-media bg-success">
                    <i className="fa fa-user-plus text-white"></i>
                  </div>
                  <div className="timeline-content">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="mr-2">
                        <span className="text-dark-75 text-hover-primary font-weight-bold">
                          Create {summary.newStudents} New Student Account(s)
                        </span>
                      </div>
                    </div>
                    <p className="text-muted mb-0">
                      New student user accounts will be created
                    </p>
                  </div>
                </div>
              )}

              {/* Enroll Students */}
              <div className="timeline-item">
                <div className="timeline-media bg-primary">
                  <i className="fa fa-graduation-cap text-white"></i>
                </div>
                <div className="timeline-content">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="mr-2">
                      <span className="text-dark-75 text-hover-primary font-weight-bold">
                        Enroll {summary?.totalStudents || 0} Student(s) in
                        Program
                      </span>
                    </div>
                  </div>
                  <p className="text-muted mb-0">
                    All students will be enrolled in {course?.course_name}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown ExpansionPanels */}
      <div className="mb-6">
        <h5 className="mb-4">Detailed Breakdown</h5>

        {/* New Students ExpansionPanel */}
        {newStudents.length > 0 && (
          <ExpansionPanel className={classes.expansionPanel}>
            <ExpansionPanelSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.expansionPanelSummary}
            >
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                New Students
                <Chip
                  label={newStudents.length}
                  size="small"
                  color="primary"
                  className={classes.chip}
                />
              </Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <div style={{ width: "100%" }}>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>DOB</th>
                        <th>Parent 1</th>
                        <th>Parent 2</th>
                        <th>Start Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newStudents.map((record) => (
                        <tr key={record.row}>
                          <td>
                            {record.student?.firstname}{" "}
                            {record.student?.lastname}
                          </td>
                          <td>{record.student?.dob}</td>
                          <td>
                            {record.parent1?.firstname}{" "}
                            {record.parent1?.lastname}
                            {record.parent1?.isNew && (
                              <Chip
                                label="New"
                                size="small"
                                color="primary"
                                className="ml-2"
                              />
                            )}
                          </td>
                          <td>
                            {record.parent2 ? (
                              <>
                                {record.parent2.firstname}{" "}
                                {record.parent2.lastname}
                                {record.parent2.isNew && (
                                  <Chip
                                    label="New"
                                    size="small"
                                    color="primary"
                                    className="ml-2"
                                  />
                                )}
                              </>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>{record.programStartDate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </ExpansionPanelDetails>
          </ExpansionPanel>
        )}

        {/* Existing Students ExpansionPanel */}
        {existingStudents.length > 0 && (
          <ExpansionPanel className={classes.expansionPanel}>
            <ExpansionPanelSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.expansionPanelSummary}
            >
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Existing Students (Re-enrollment)
                <Chip
                  label={existingStudents.length}
                  size="small"
                  color="default"
                  className={classes.chip}
                />
              </Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <div style={{ width: "100%" }}>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>DOB</th>
                        <th>Parent 1</th>
                        <th>Parent 2</th>
                        <th>Existing Parents</th>
                        <th>Start Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {existingStudents.map((record) => (
                        <tr key={record.row}>
                          <td>
                            {record.student?.firstname}{" "}
                            {record.student?.lastname}
                            <Chip
                              label="Existing"
                              size="small"
                              color="default"
                              className="ml-2"
                            />
                          </td>
                          <td>{moment(record.student?.dob).format("YYYY-MM-DD")}</td>
                          <td>
                            {record.parent1?.firstname}{" "}
                            {record.parent1?.lastname}
                            {record.parent1?.isNew && (
                              <Chip
                                label="New"
                                size="small"
                                color="primary"
                                className="ml-2"
                              />
                            )}
                          </td>
                          <td>
                            {record.parent2 ? (
                              <>
                                {record.parent2.firstname}{" "}
                                {record.parent2.lastname}
                                {record.parent2.isNew && (
                                  <Chip
                                    label="New"
                                    size="small"
                                    color="primary"
                                    className="ml-2"
                                  />
                                )}
                              </>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            {record.existingParents &&
                            record.existingParents.length > 0 ? (
                              <div>
                                {record.existingParents.map((p, idx) => (
                                  <div key={idx}>
                                    {p.firstname} {p.lastname}
                                    {p.primary_ind === 1 && (
                                      <Chip
                                        label="Primary"
                                        size="small"
                                        className="ml-1"
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">None</span>
                            )}
                          </td>
                          <td>{record.programStartDate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </ExpansionPanelDetails>
          </ExpansionPanel>
        )}

        {/* Students with Warnings ExpansionPanel */}
        {studentsWithWarnings.length > 0 && (
          <ExpansionPanel className={classes.expansionPanel} defaultExpanded>
            <ExpansionPanelSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.expansionPanelSummary}
              style={{ backgroundColor: "#fff4e5" }}
            >
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                <i className="fa fa-exclamation-triangle text-warning mr-2"></i>
                Students with Warnings
                <Chip
                  label={studentsWithWarnings.length}
                  size="small"
                  style={{ backgroundColor: "#ff9800", color: "white" }}
                  className={classes.chip}
                />
              </Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <div style={{ width: "100%" }}>
                {studentsWithWarnings.map((record) => {
                  const studentWarnings = warnings.filter(
                    (w) => w.row === record.row
                  );
                  return (
                    <div key={record.row} className="mb-4 p-3 border rounded">
                      <div className="d-flex justify-content-between mb-3">
                        <h6 className="mb-0">
                          Row {record.row}: {record.student?.firstname}{" "}
                          {record.student?.lastname}
                        </h6>
                      </div>
                      {studentWarnings.map((warning, idx) => (
                        <div key={idx} className="alert alert-warning mb-2">
                          <strong>{warning.message}</strong>
                          <p className="mb-0 mt-1 small">{warning.details}</p>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </ExpansionPanelDetails>
          </ExpansionPanel>
        )}
      </div>

      {/* Confirmation Alert */}
      <div className="alert alert-light-primary mb-6">
        <div className="alert-icon">
          <i className="flaticon-info"></i>
        </div>
        <div className="alert-text">
          <strong>Ready to Process</strong>
          <p className="mb-0 mt-2">
            By clicking "Confirm & Process", you agree to create the accounts
            and enrollments as described above. This action cannot be undone.
          </p>
        </div>
      </div>

      {feeProblem && (
        <div className="alert alert-custom alert-light-danger mb-6">
          <div className="alert-text">{feeProblem}</div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="d-flex justify-content-between">
        <button className="btn btn-secondary btn-lg" onClick={handleBack}>
          <i className="fa fa-arrow-left mr-2"></i>
          Back to Validation
        </button>
        <button
          className="btn btn-success btn-lg"
          onClick={handleConfirm}
          disabled={!!feeProblem}
        >
          <i className="fa fa-check mr-2"></i>
          Confirm & Process
        </button>
      </div>
    </div>
  );
};
